import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLang } from '../../contexts/LangContext';
import { settingsApi, LogoSettings } from '../../services/settingsApi';
import { ImageUploadField } from '../common/ImageUploadField';
import { MediaImage } from '../common/MediaImage';

interface LogoSettingsPanelProps {
  settingsType: 'global' | 'user';
  setSettingsType: (type: 'global' | 'user') => void;
  isSuperAdmin: boolean;
}

export const LogoSettingsPanel: React.FC<LogoSettingsPanelProps> = ({
  settingsType,
  setSettingsType,
  isSuperAdmin,
}) => {
  const { t } = useLang();
  const [logoSettings, setLogoSettings] = useState<LogoSettings | null>(null);
  const [effectiveLogo, setEffectiveLogo] = useState<LogoSettings | null>(null);
  const [hasOwnLogo, setHasOwnLogo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      if (settingsType === 'global') {
        const current = await settingsApi.getLogoSettings(true);
        setLogoSettings(current);
        setEffectiveLogo(null);
        setHasOwnLogo(!!current?.logoPath);
      } else {
        const own = await settingsApi.getUserLogoSettings();
        setHasOwnLogo(!!own?.logoPath);
        setLogoSettings(own);
        const effective = await settingsApi.getEffectiveLogoSettings();
        setEffectiveLogo(effective);
      }
    } catch (error) {
      console.error('Failed to load logo settings:', error);
      toast.error(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [settingsType]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const saved = await settingsApi.uploadLogo(file, settingsType === 'global');
      setLogoSettings(saved);
      setHasOwnLogo(true);
      toast.success(t('logoSettingsSaved'));
      loadSettings();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('logoSettingsSaveFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('confirmDeleteLogoSettings'))) {
      return;
    }

    setLoading(true);
    try {
      await settingsApi.deleteLogoSettings(settingsType === 'global');
      setLogoSettings(null);
      setHasOwnLogo(false);
      toast.success(t('logoSettingsDeleted'));
      loadSettings();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('logoSettingsDeleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const previewUrl = logoSettings?.logoUrl || logoSettings?.logoPath || '';

  return (
    <div className="space-y-6">
      <div className="space-y-4 border-b border-gray-200 pb-4">
        <div className="flex space-x-4">
          {isSuperAdmin && (
            <button
              onClick={() => setSettingsType('global')}
              className={`px-4 py-2 rounded-md ${
                settingsType === 'global'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('globalSettings')}
            </button>
          )}
          <button
            onClick={() => setSettingsType('user')}
            className={`px-4 py-2 rounded-md ${
              settingsType === 'user'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('personalSettings')}
          </button>
        </div>

        {settingsType === 'global' && isSuperAdmin && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
            {t('logoSettingsGlobalHint')}
          </p>
        )}

        {settingsType === 'user' && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
            {t('logoSettingsOperatorHint')}
          </p>
        )}
      </div>

      {settingsType === 'user' && effectiveLogo && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="font-medium text-blue-900 mb-2">{t('logoSettingsEffectiveTitle')}</h4>
          <div className="flex items-center gap-4">
            {effectiveLogo.logoPath && (
              <MediaImage
                src={effectiveLogo.logoUrl || effectiveLogo.logoPath}
                alt="Effective logo"
                className="w-16 h-16 object-contain rounded-lg border border-blue-200 bg-white p-1"
                fallbackClassName="w-16 h-16 rounded-lg border border-blue-200 bg-white"
              />
            )}
            <p className="text-sm text-blue-700">
              {hasOwnLogo ? t('logoSettingsUsingOperator') : t('logoSettingsUsingGlobalFallback')}
            </p>
          </div>
        </div>
      )}

      {settingsType === 'user' && !hasOwnLogo && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
          {t('logoSettingsNoOwnYet')}
        </p>
      )}

      <ImageUploadField
        label={t('logoSettingsUploadLabel')}
        imageUrl={previewUrl}
        uploading={uploading}
        disabled={loading}
        onUpload={handleUpload}
        hint={t('logoSettingsUploadHint')}
        fit="contain"
      />

      <div className="flex flex-wrap gap-4">
        <button
          onClick={handleDelete}
          disabled={loading || uploading || !hasOwnLogo}
          className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 disabled:opacity-50"
        >
          {t('deleteSettings')}
        </button>
      </div>
    </div>
  );
};
